import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Item, useCanvasStore } from '@/stores/canvasStore';
import { GripHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { detectColorInText, getContrastColor } from '@/lib/colorUtils';

interface StickyNoteProps {
  item: Item;
  onHeightChange?: (newHeight: number) => void;
}

interface StickyContent {
  text?: string;
  color?: string;
  customColor?: string;
  colorCode?: string;
}

// Map color names to CSS variables
const DEFAULT_COLOR = 'yellow';

export function StickyNote({ item, onHeightChange }: StickyNoteProps) {
  const content = item.content as StickyContent;
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(content.text || '');
  const [isHovering, setIsHovering] = useState(false);
  const isEditingRef = useRef(false);
  const [hoverZone, setHoverZone] = useState<'drag' | 'content' | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastHeightRef = useRef(item.height);
  const onHeightChangeRef = useRef(onHeightChange);
  onHeightChangeRef.current = onHeightChange;
  const { updateItem, newlyCreatedItemId, clearNewlyCreatedItem, setIsEditingItem } = useCanvasStore();
  
  // Ensure color defaults to yellow if not specified
  const color = content.color || DEFAULT_COLOR;
  const customColor = content.customColor;
  const colorCode = content.colorCode;

  // Notify store when editing state changes
  useEffect(() => {
    setIsEditingItem(isEditing);
    isEditingRef.current = isEditing;
  }, [isEditing, setIsEditingItem]);

  // Re-sync local text when item content changes externally (e.g., color change, AI insert)
  useEffect(() => {
    if (!isEditingRef.current && content.text !== undefined) {
      setText(content.text || '');
    }
  }, [content.text]);

  // Auto-edit when newly created
  useEffect(() => {
    if (newlyCreatedItemId === item.id) {
      setIsEditing(true);
      clearNewlyCreatedItem();
    }
  }, [newlyCreatedItemId, item.id, clearNewlyCreatedItem]);

  // Focus and resize textarea when editing starts
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      adjustTextareaHeight();
    }
  }, [isEditing]);

  // Adjust textarea height to content
  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, []);

  // Check content height and update item height if needed
  const checkContentHeight = useCallback(() => {
    if (!onHeightChangeRef.current) return;
    
    const contentEl = isEditing ? textareaRef.current : displayRef.current;
    if (!contentEl) return;
    
    const contentHeight = contentEl.scrollHeight;
    const padding = 24; // p-3 = 12px * 2
    const minHeight = 100;
    const requiredHeight = Math.max(minHeight, contentHeight + padding);
    
    // Update height if it's different (both grow and shrink)
    if (Math.abs(requiredHeight - lastHeightRef.current) > 5) {
      lastHeightRef.current = requiredHeight;
      onHeightChangeRef.current(requiredHeight);
    }
  }, [isEditing]);

  // Check height on mount and when text changes
  useEffect(() => {
    checkContentHeight();
  }, [text, checkContentHeight]);

  const handleBlur = () => {
    setIsEditing(false);
    
    // Detect color in text
    const { color: detectedColor, cleanedText } = detectColorInText(text);
    
    if (detectedColor) {
      // Apply custom color and store the color code
      setText(cleanedText);
      updateItem(item.id, {
        content: {
          ...content,
          text: cleanedText,
          color: 'custom',
          customColor: detectedColor,
          colorCode: detectedColor,
        },
      });
    } else if (text !== content.text) {
      updateItem(item.id, {
        content: { ...content, text },
      });
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    adjustTextareaHeight();
    // Use requestAnimationFrame to check after render
    requestAnimationFrame(() => checkContentHeight());
  };

  // Determine which zone the mouse is in (top/bottom 15% = drag, middle 70% = content)
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current || isEditing) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const heightPercent = y / rect.height;
    
    if (heightPercent < 0.15 || heightPercent > 0.85) {
      setHoverZone('drag');
    } else {
      setHoverZone('content');
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // If in content zone and not already editing, start editing
    if (hoverZone === 'content' && !isEditing) {
      e.stopPropagation();
      setIsEditing(true);
    }
  };

  // Get cursor based on zone
  const getCursor = () => {
    if (isEditing) return 'text';
    if (hoverZone === 'drag') return 'grab';
    if (hoverZone === 'content') return 'text';
    return 'default';
  };

  return (
    <div
      ref={containerRef}
      className="w-full h-full rounded-lg card-shadow transition-all duration-200 relative group overflow-hidden"
      style={{ 
        cursor: getCursor(),
        backgroundColor: customColor 
          ? customColor 
          : `hsl(var(--sticky-${color}))`,
        color: customColor 
          ? getContrastColor(customColor) 
          : 'hsl(var(--sticky-text))',
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => { setIsHovering(false); setHoverZone(null); }}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
      data-drag-zone={hoverZone === 'drag' ? 'true' : 'false'}
    >
      {/* Top drag handle indicator */}
      {isHovering && !isEditing && (
        <div className="absolute top-0 left-0 right-0 h-[15%] min-h-[16px] flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
          <div className="bg-foreground/10 rounded-full px-2 py-0.5">
            <GripHorizontal className="w-4 h-3 text-foreground/40" />
          </div>
        </div>
      )}

      {/* Content area */}
      <div className="w-full h-full p-3 overflow-hidden">
        {isEditing ? (
          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleTextChange}
            onBlur={handleBlur}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            className="w-full h-full bg-transparent resize-none border-none outline-none text-sm leading-relaxed overflow-hidden"
            style={{ 
              minHeight: '1.5em',
              color: 'inherit',
            }}
            placeholder="Type here..."
          />
        ) : (
          <div 
            ref={displayRef}
            className="w-full text-sm whitespace-pre-wrap leading-relaxed break-words"
            style={{ color: 'inherit' }}
          >
            {text || <span style={{ opacity: 0.5, fontStyle: 'italic' }}>Click to edit...</span>}
          </div>
        )}
      </div>

      {/* Bottom drag handle indicator */}
      {isHovering && !isEditing && (
        <div className="absolute bottom-0 left-0 right-0 h-[15%] min-h-[16px] flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity pointer-events-none">
          <div className="bg-foreground/10 rounded-full px-2 py-0.5">
            <GripHorizontal className="w-4 h-3 text-foreground/40" />
          </div>
        </div>
      )}

      {/* Color code label — bottom right */}
      {colorCode && !isEditing && (
        <div
          className="absolute bottom-1.5 right-2 text-[10px] font-mono select-none pointer-events-none"
          style={{ opacity: 0.35, color: 'inherit' }}
        >
          {colorCode}
        </div>
      )}

    </div>
  );
}
